//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

// Displayed after a successful SSO sign-in via the CLI (con auth). Shows the one-time code
// so the user can copy it manually, and fires a fetch() to the CLI's local callback listener
// when callback_port is present — avoiding a redirect to the CLI's plain HTML page.

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useIntl } from "~base/Internationalization/IntlContext";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import { Button } from "~base/Components/Atoms/Button";
import { useStyles } from "./Styles";
import { reducer } from "./Store";
import { SplashPage } from "~base/Pages/Shared/SplashPage";
import { SplashContent } from "~base/Pages/Shared/SplashContent";
import TextBlock from "~base/Components/Primitives/TextBlock";

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

export const ExternalApiCodePage: React.FunctionComponent = () => {
    const [searchParams] = useSearchParams();
    const code = searchParams.get("code") ?? "";
    const oauthState = searchParams.get("state") ?? "";
    const callbackPort = searchParams.get("callback_port");

    const [pageState, dispatch, _] = useConsilioReducer(
        buildReducerId("ExternalApiCodePage"),
        reducer,
        { callbackStatus: "idle", copied: false }
    );

    const classes = useStyles();
    const intl = useIntl();

    useEffect(() => {
        if (!callbackPort) return;

        let cancelled = false;
        dispatch({ type: "startCallback" });

        fetch(
            `http://localhost:${callbackPort}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(oauthState)}`
        )
            .then(() => {
                if (!cancelled) dispatch({ type: "callbackSuccess" });
            })
            .catch(() => {
                if (!cancelled) dispatch({ type: "callbackFailed" });
            });

        return () => {
            cancelled = true;
        };
    }, [dispatch, callbackPort, code, oauthState]);

    const callbackSucceeded = pageState.callbackStatus === "success";

    const handleCopy = () => {
        navigator.clipboard
            .writeText(code)
            .then(() => dispatch({ type: "setCopied" }));
    };

    return (
        <SplashPage isLoading={false} showSplashScreen={false}>
            <SplashContent
                header={
                    <Rubrik
                        textCode="VisiFinance.UI.ExternalApiCode.Title"
                        rubrikSizeType="Medium"
                    />
                }
                footer={
                    !callbackSucceeded && (
                        <Button
                            testIdentifier={"ExternalApiCodeCopyButton"}
                            onClick={handleCopy}
                        >
                            {pageState.copied
                                ? intl.formatText(
                                      "VisiFinance.UI.ExternalApiCode.Copied"
                                  )
                                : intl.formatText(
                                      "VisiFinance.UI.ExternalApiCode.CopyButton"
                                  )}
                        </Button>
                    )
                }
            >
                {!callbackSucceeded && (
                    <>
                        <TextBlock
                            styleType="Paragraph"
                            textCode="VisiFinance.UI.ExternalApiCode.CopyDescription"
                        />
                        <pre className={classes.codeBlock}>{code}</pre>
                    </>
                )}
                {pageState.callbackStatus === "success" && (
                    <TextBlock
                        styleType="Paragraph"
                        textCode="VisiFinance.UI.ExternalApiCode.CallbackSuccess"
                    />
                )}
                {pageState.callbackStatus === "failed" && (
                    <TextBlock
                        styleType="Paragraph"
                        styleSubType="Validation"
                        textCode="VisiFinance.UI.ExternalApiCode.CallbackFailed"
                    />
                )}
            </SplashContent>
        </SplashPage>
    );
};
